import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { BaseComponent } from '../../base.component';

@Component({
  selector: 'ui-dialog',
  standalone: true,
  imports: [CommonModule, MatIcon],
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
})
export class DialogComponent extends BaseComponent {
  @Input() centerTitle: boolean = false;
  @Input() isCloseable: boolean = true;
  @Output() onDismiss = new EventEmitter<void>();

  @ViewChild('dialogContainer', { static: true }) dialogContainer!: ElementRef;
  private previouslyFocusedElement: HTMLElement | null = null;

  /** Id univoco per istanza: due dialog aperti insieme non devono condividerlo */
  readonly titleId = `ui-dialog-title-${Math.random().toString(36).slice(2, 9)}`;

  override ngAfterViewInit() {
    // Save the element that was focused before opening the dialog
    this.previouslyFocusedElement = document.activeElement as HTMLElement;
    // Focus the dialog container
    if (this.dialogContainer && this.dialogContainer.nativeElement) {
      this.dialogContainer.nativeElement.focus();
    }
    // Trap focus inside dialog
    this.trapFocus();
  }

  /**
   * Il focus va restituito in *tutti* i casi di chiusura, non solo quando si passa
   * da `dismissDialog()`: i pulsanti di conferma/annulla dei chiamanti nascondono
   * il dialog direttamente, e prima il focus finiva sul body.
   */
  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.restoreFocus();
  }

  dismissDialog() {
    this.onDismiss.emit();
  }

  private restoreFocus(): void {
    const target = this.previouslyFocusedElement;
    this.previouslyFocusedElement = null;
    if (!target) return;
    setTimeout(() => {
      // Se l'elemento non è più nel documento (es. l'azione ha svuotato la lista)
      // tocca al chiamante decidere dove portare il focus.
      if (!document.contains(target)) return;
      const active = document.activeElement;
      if (active && active !== document.body && target.contains(active)) return;
      target.focus();
    }, 0);
  }

  private static readonly FOCUSABLE_SELECTORS =
    'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  private trapFocus() {
    const dialog = this.dialogContainer?.nativeElement;
    if (!dialog) return;
    // L'elenco viene ricalcolato ad ogni Tab (non catturato una sola volta all'apertura):
    // se il contenuto proiettato cambia dinamicamente, il trap resta corretto.
    dialog.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusableElements = dialog.querySelectorAll(DialogComponent.FOCUSABLE_SELECTORS);
      if (focusableElements.length === 0) return;
      const first = focusableElements[0] as HTMLElement;
      const last = focusableElements[focusableElements.length - 1] as HTMLElement;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }
}
